import { defineStore } from 'pinia';
import { StoreNames } from '@/constants/stores';
import { ref } from 'vue';
import { deleteLSItem, getLSItem, setLSItem } from '@/service/NimbusLSService';
import UserAuthenticationService from '@/service/UserAuthenticationService';
import { logoutCurrent } from '@/service/UserService';
import router from '@/routes';
import { logoutReasons } from '@/constants/session';
import { useRoute } from 'vue-router';


export const useSessionStore = defineStore(
    StoreNames.SESSION,
    () => {
        const route = useRoute();

        /**
         * States
         */
        const logoutReason = ref(null);

        const initLogoutReason = () => {
            const storedReason = getLSItem('nimbus:logout-reason');

            if (storedReason) {
                try {
                    logoutReason.value = JSON.parse(storedReason);
                } catch (e) {
                    logoutReason.value = { reason: storedReason, ts: Date.now() };
                }
            }
        };

        /**
         * Getters
         */
        const getLogoutMessage = () => {
            if (!logoutReason.value) return null;

            const reasonKey = typeof logoutReason.value === 'string'
                ? logoutReason.value
                : logoutReason.value.reason;

            const key = reasonKey.toUpperCase();

            // return a mapped message or original (custom) message
            return logoutReasons[key] || reasonKey;
        };

        /**
         * Actions
         */
        const setLoggedIn = () => {
            logoutReason.value = null;

            deleteLSItem('nimbus:logout-reason');
        };

        const setLogoutReason = (reason) => {
            const payload = typeof reason === 'string'
                ? { reason, ts: Date.now() }
                : reason;

            logoutReason.value = payload;
            setLSItem('nimbus:logout-reason', JSON.stringify(payload));
        };

        /**
         * Logout the user
         *
         * @param {string}  reason  - idle, manual, expired
         * @param {boolean} broadcast - notify other tabs
         */
        const logout = async (reason = 'manual', broadcast = true) => {
            const payload = {
                reason,
                ts: Date.now(),
            };

            logoutReason.value = payload;

            // Store reason for the login screen.
            setLSItem('nimbus:logout-reason', JSON.stringify(payload));

            // Notify other tabs.
            if (broadcast) {
                setLSItem('nimbus:logout-reason', JSON.stringify(payload));
            }

            await _cleanup();

            // When logout manually, just redirect to the 'login' route
            if (reason === 'manual') {
                await router.push({ name: 'login' });
            } else {
                // Otherwise, redirect to the last page
                await router.push({
                    name: 'login',
                    query: {
                        redirect: route.path,
                    },
                });
            }
        };

        async function _cleanup() {
            const auth = new UserAuthenticationService();

            try {
                await logoutCurrent();
            } catch (_) {
                //
            }

            auth.setBearer(null);
            auth.removePermissions();
            auth.removeAuthenticatedUserID();
            auth.setTwoFactorAuthentication();

            for (let key of Object.keys(localStorage)) {
                if (key.includes('translation') && !key.includes('nimbus-selected-language')) {
                    deleteLSItem(key);
                }
            }
        }

        return {
            /** States */
            logoutReason,

            /** Getters */
            getLogoutMessage,

            /** Actions */
            setLoggedIn,
            setLogoutReason,
            logout,
            initLogoutReason,
        };
    }, {
        persist: true,
    });